import { CurioQuiz } from "../components/CurioQuiz";
import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ChevronRight,
  ExternalLink,
  Twitter,
  Facebook,
  Linkedin,
  Link2,
  Check,
  MessageCircle,
  ShieldCheck,
} from "lucide-react";
import api from "../lib/api";
import { Layout } from "../components/Layout";
import { SocialPost } from "../components/SocialPost";
import { FavouriteButton } from "../components/FavouriteButton";
import { useAuth } from "../context/AuthContext";
import { useVisitor } from "../context/VisitorAuthContext";

const ADSENSE_CLIENT = process.env.REACT_APP_ADSENSE_CLIENT;
const ADSENSE_SLOT = process.env.REACT_APP_ADSENSE_SLOT;

const AdSenseBox = () => {
  const live = ADSENSE_CLIENT && ADSENSE_SLOT;
  const insRef = useRef(null);

  useEffect(() => {
    if (!live) return;
    // Inject the AdSense loader once.
    if (!document.querySelector("script[data-cv-adsense]")) {
      const s = document.createElement("script");
      s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`;
      s.async = true;
      s.crossOrigin = "anonymous";
      s.setAttribute("data-cv-adsense", "1");
      document.head.appendChild(s);
    }
    if (!insRef.current || insRef.current.getAttribute("data-adsbygoogle-status")) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (e) {}
  }, [live]);

  if (live) {
    return (
      <div data-testid="adsense-unit" className="bg-[#F4EFE6] border border-[#CBBDA8] rounded-sm p-2">
        <ins
          ref={insRef}
          className="adsbygoogle"
          style={{ display: "block", minHeight: 250 }}
          data-ad-client={ADSENSE_CLIENT}
          data-ad-slot={ADSENSE_SLOT}
          data-ad-format="auto"
          data-full-width-responsive="true"
        />
      </div>
    );
  }

  return (
    <div
      data-testid="adsense-placeholder"
      className="bg-[#F4EFE6] border border-dashed border-[#CBBDA8] rounded-sm p-4 text-center"
    >
      <p className="caption-label !text-[9px] mb-3">Sponsor Exhibition</p>
      <div className="flex items-center justify-center h-[250px] bg-white/60 border border-[#D8CEB9] rounded-sm">
        <div>
          <p className="font-mono-cv text-xs text-[#B8AC9C]">GOOGLE ADSENSE</p>
          <p className="font-mono-cv text-[10px] text-[#B8AC9C] mt-1">300 × 250 placeholder</p>
          <p className="font-mono-cv text-[9px] text-[#CBBDA8] mt-2 px-4">Add publisher &amp; slot IDs<br />to go live</p>
        </div>
      </div>
    </div>
  );
};

const ShareButtons = ({ fact }) => {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== "undefined" ? window.location.href : "";
  const text = fact.social_caption || fact.hook;

  const links = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(text + " " + url)}`,
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error("Copy to clipboard failed:", e);
    }
  };

  const btn = "w-10 h-10 flex items-center justify-center rounded-full border border-[#D8CEB9] text-[#4A4036] hover:bg-[#9E4535] hover:text-[#FAF7F0] hover:border-[#9E4535] transition-colors";

  return (
    <div className="flex items-center gap-2" data-testid="share-buttons">
      <a href={links.twitter} target="_blank" rel="noopener noreferrer" className={btn} data-testid="share-twitter" aria-label="Share on X"><Twitter className="w-4 h-4" /></a>
      <a href={links.facebook} target="_blank" rel="noopener noreferrer" className={btn} data-testid="share-facebook" aria-label="Share on Facebook"><Facebook className="w-4 h-4" /></a>
      <a href={links.linkedin} target="_blank" rel="noopener noreferrer" className={btn} data-testid="share-linkedin" aria-label="Share on LinkedIn"><Linkedin className="w-4 h-4" /></a>
      <a href={links.whatsapp} target="_blank" rel="noopener noreferrer" className={btn} data-testid="share-whatsapp" aria-label="Share on WhatsApp"><MessageCircle className="w-4 h-4" /></a>
      <button onClick={copy} className={btn} data-testid="share-copy" aria-label="Copy link">
        {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
      </button>
    </div>
  );
};

const FactPage = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const { consume } = useVisitor();
  const [fact, setFact] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    let active = true;
    setLocked(false);
    consume("curio", slug).then((ok) => { if (active) setLocked(!ok); });
    return () => { active = false; };
    // eslint-disable-next-line
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setFact(null);
    setNotFound(false);
    api
      .get(`/facts/${slug}`)
      .then((r) => setFact(r.data))
      .catch((e) => {
        console.error("Failed to load fact:", e);
        setNotFound(true);
      });
  }, [slug]);

  useEffect(() => {
    if (fact) {
      document.title = `${fact.title} · CurioVault`;
      const setMeta = (name, content, attr = "name") => {
        let el = document.querySelector(`meta[${attr}="${name}"]`);
        if (!el) {
          el = document.createElement("meta");
          el.setAttribute(attr, name);
          document.head.appendChild(el);
        }
        el.setAttribute("content", content);
      };
      setMeta("description", fact.hook);
      setMeta("og:title", fact.title, "property");
      setMeta("og:description", fact.hook, "property");
      setMeta("og:image", fact.image_url, "property");
      const canonicalUrl = `${window.location.origin}/curio/${slug}`;
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }
      canonical.setAttribute("href", canonicalUrl);
    }
    return () => { document.title = "CurioVault"; };
  }, [fact]);

  if (locked) {
    return (
      <Layout>
        <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="curio-locked">
          <h1 className="font-serif-display text-3xl font-bold text-[#9E4535]">Daily free limit reached</h1>
          <p className="font-body text-[#4A4036] mt-3">Free readers enjoy 10 curios a day. Join CurioVault Club for unlimited access to all 100 curios and the full Pub Quiz.</p>
          <Link to="/club" data-testid="curio-locked-club" className="inline-block mt-6 bg-[#9E4535] text-[#FAF7F0] font-ui font-semibold px-6 py-3 rounded-sm hover:bg-[#53131b] transition-colors">Join the Club</Link>
          <Link to="/" className="block mt-3 font-ui text-sm text-[#9E4535] hover:underline">Return to the vault</Link>
        </div>
      </Layout>
    );
  }

  if (notFound) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <h1 className="font-serif-display text-3xl font-bold text-[#9E4535]">Curio not found</h1>
          <Link to="/" className="font-ui text-sm text-[#9E4535] hover:underline mt-4 inline-block">Return to the vault</Link>
        </div>
      </Layout>
    );
  }

  if (!fact) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto px-6 py-24 text-center font-body text-[#7A6C5D]">Retrieving specimen…</div>
      </Layout>
    );
  }

  const HEADINGS = ["Why it happens", "How we know", "Did you know?"];
  const paragraphs = fact.full_fact.split(/\n\s*\n/).flatMap((p) => {
    const [first, ...rest] = p.trim().split("\n");
    return HEADINGS.includes(first.trim()) ? [{ h: first.trim() }, ...(rest.length ? [rest.join(" ")] : [])] : [p.trim()];
  });

  const longEnough = fact.full_fact.trim().split(/\s+/).length > 200;
  const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : null);
  const reviewed = fmt(fact.last_reviewed_at || fact.sources_checked_at || fact.updated_at || fact.created_at);
  const updated = fmt(fact.updated_at || fact.created_at);

  return (
    <Layout ads={longEnough}>
      <article className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 font-mono-cv text-xs text-[#7A6C5D] mb-8 flex-wrap" data-testid="breadcrumb">
          <Link to="/" className="hover:text-[#9E4535]">Vault</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to={`/subject/${fact.subject_slug}`} className="hover:text-[#9E4535]">{fact.subject?.name}</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-[#1A1615] truncate max-w-[180px]">{fact.title}</span>
        </nav>

        <div className="grid lg:grid-cols-[1fr_300px] gap-10 lg:gap-14">
          {/* Main column */}
          <div className="min-w-0">
            <Link to={`/subject/${fact.subject_slug}`} className="caption-label !text-[#9E4535] hover:underline">
              {fact.subject?.name}
            </Link>
            <h1 data-testid="fact-title" className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A1615] leading-[1.1] mt-3">
              {fact.title}
            </h1>
            <p className="font-body italic text-lg sm:text-xl text-[#9E4535] mt-5 leading-relaxed border-l-4 border-[#C5A059] pl-5">
              {fact.hook}
            </p>
            {fact.source_status === "verified" && (
              <a href="#fact-sources" data-testid="fact-checked-badge" title="At least one cited source has been checked and is live"
                className="mt-4 inline-flex items-center gap-1.5 font-mono-cv text-[11px] uppercase tracking-wider text-[#2F6B33] bg-[#E7F1E4] border border-[#BCD5B5] px-2.5 py-1 rounded-full hover:bg-[#DCEBD8] transition-colors">
                <ShieldCheck className="w-3.5 h-3.5" /> Fact-checked · {fact.verified_source_count} verified source{fact.verified_source_count === 1 ? "" : "s"}
              </a>
            )}
            {reviewed && (
              <p data-testid="fact-reviewed-line" className="mt-3 font-mono-cv text-[11px] text-[#7A6C5D]">
                Reviewed by the CurioVault curator · {reviewed}{updated && updated !== reviewed ? ` · Last updated ${updated}` : ""} ·{" "}
                <Link to="/editorial-standards" data-testid="fact-standards-link" className="underline hover:text-[#9E4535]">Editorial standards</Link>
              </p>
            )}
            <figure className="mt-8">
              {fact.video_url ? (
                <div className="relative mx-auto w-full max-w-[420px] aspect-[9/16] overflow-hidden rounded-sm border border-[#D8CEB9] bg-[#1A1615]">
                  <video
                    src={fact.video_url}
                    data-testid="fact-hero-video"
                    className="w-full h-full object-cover"
                    autoPlay
                    muted
                    loop
                    playsInline
                    controls
                    poster={fact.image_url || undefined}
                  />
                </div>
              ) : (
                <div className="relative aspect-[3/2] overflow-hidden rounded-sm border border-[#D8CEB9] bg-[#F3EDE0]">
                  <img src={fact.image_url || undefined} alt={fact.image_alt || `${fact.title} — AI illustration`} className="w-full h-full object-cover" />
                </div>
              )}
              <figcaption data-testid="fact-image-caption" className="font-mono-cv text-[11px] text-[#7A6C5D] mt-2 text-center">
                {fact.video_url ? "Motion curio — generated for CurioVault" : "Image generated by CurioVault AI — copyright free"}
              </figcaption>
            </figure>

            {/* Share row */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-6 py-4 border-y border-[#D8CEB9]">
              <div className="flex items-center gap-4">
                <FavouriteButton slug={fact.slug} variant="button" />
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                <p className="caption-label">Share this curiosity</p>
                <ShareButtons fact={fact} />
              </div>
            </div>

            {/* Body */}
            <div className="mt-8 space-y-5" data-testid="fact-body">
              {paragraphs.map((p, i) => (p.h ? (
                <h2 key={`h-${i}`} className="font-serif-display text-xl sm:text-2xl font-bold text-[#1A1615] pt-3" data-testid={`fact-section-${i}`}>{p.h}</h2>
              ) : (
                <p key={`para-${i}-${p.slice(0, 24)}`} className={`font-body text-base sm:text-lg text-[#2E282A] leading-[1.85] ${i === 0 ? "drop-cap" : ""}`}>
                  {p}
                </p>
              )))}
            </div>

            <CurioQuiz slug={fact.slug} />

            {/* Tags */}
            {fact.tags?.length > 0 && (
              <div className="mt-10">
                <p className="caption-label mb-3">Tagged</p>
                <div className="flex flex-wrap gap-2" data-testid="fact-tags">
                  {fact.tags.map((t) => (
                    <Link
                      key={t}
                      to={`/subject/${fact.subject_slug}`}
                      data-testid={`tag-pill-${t}`}
                      className="font-ui text-xs font-medium px-3 py-1.5 rounded-full bg-[#F5E8EA] text-[#9E4535] border border-[#E4C9CD] hover:bg-[#9E4535] hover:text-[#FAF7F0] transition-colors"
                    >
                      #{t}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Sources & Further Reading */}
            {(fact.sources?.length > 0 || fact.source_url) && (
              <div id="fact-sources" className="mt-8 bg-[#F3EDE0] border border-[#D8CEB9] rounded-sm p-5" data-testid="fact-sources">
                <p className="caption-label mb-3">Sources &amp; Further Reading</p>
                <ul className="space-y-2 list-disc pl-5">
                  {(fact.sources?.length > 0
                    ? fact.sources
                    : [{ label: fact.source_url, url: fact.source_url }]
                  ).map((s, i) => (
                    <li key={s.url || `src-${i}`} className="font-body text-sm text-[#4A4036] marker:text-[#9E4535]">
                      {s.url ? (
                        <a
                          href={s.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`source-link-${i}`}
                          className="text-[#9E4535] hover:underline inline-flex items-center gap-1.5 break-words"
                        >
                          {s.label || s.url} <ExternalLink className="w-3.5 h-3.5 shrink-0" />
                        </a>
                      ) : (
                        <span data-testid={`source-link-${i}`} className="break-words">{s.label}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Connected Curiosities */}
            <section className="mt-16 pt-10 border-t-2 border-[#9E4535]" data-testid="connected-curiosities">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-2 h-2 rounded-full bg-[#C5A059]" />
                <h2 className="font-serif-display text-2xl sm:text-3xl font-bold text-[#1A1615]">Connected Curiosities</h2>
              </div>
              {fact.related_facts?.length > 0 ? (
                <div className="grid sm:grid-cols-2 gap-5">
                  {fact.related_facts.map((rf) => (
                    <Link
                      key={rf.slug}
                      to={`/curio/${rf.slug}`}
                      data-testid={`related-fact-${rf.slug}`}
                      className="group flex gap-4 bg-white border border-[#D8CEB9] rounded-sm p-3 hover:border-[#9E4535] transition-colors"
                    >
                      <div className="w-20 h-20 shrink-0 overflow-hidden rounded-sm bg-[#F3EDE0]">
                        <img src={rf.image_url || undefined} alt={`${rf.title} — AI illustration`} className="w-full h-full object-cover" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-serif-display text-base font-semibold text-[#1A1615] leading-snug group-hover:text-[#9E4535] transition-colors line-clamp-2">
                          {rf.title}
                        </h3>
                        <p className="font-body text-xs text-[#4A4036] mt-1 line-clamp-2">{rf.hook}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="font-body text-[#4A4036]">No connections catalogued yet.</p>
              )}
            </section>

            {/* Copyright / originality note */}
            <p
              data-testid="fact-copyright-note"
              className="mt-12 pt-6 border-t border-[#D8CEB9] font-body text-xs text-[#7A6C5D] italic leading-relaxed"
            >
              This curio is written in original words for CurioVault. Facts themselves are
              not copyrightable, but our wording is. If you reuse, please credit{" "}
              <Link to="/copyright-policy" className="text-[#9E4535] hover:underline not-italic">CurioVault.co</Link>.
            </p>
          </div>

          {/* Sidebar */}
          <aside className="lg:sticky lg:top-24 self-start space-y-6">
            {user && (
              <SocialPost caption={fact.social_caption} url={typeof window !== "undefined" ? window.location.href : ""} variant="panel" />
            )}
            {longEnough && <AdSenseBox />}
            <div className="bg-white border border-[#D8CEB9] rounded-sm p-5">
              <p className="caption-label mb-3">In this exhibit</p>
              <dl className="space-y-3 font-ui text-sm">
                <div>
                  <dt className="text-[#7A6C5D] text-xs uppercase tracking-wide">Subject</dt>
                  <dd className="text-[#1A1615] mt-0.5">
                    <Link to={`/subject/${fact.subject_slug}`} className="hover:text-[#9E4535]">{fact.subject?.name}</Link>
                  </dd>
                </div>
                <div>
                  <dt className="text-[#7A6C5D] text-xs uppercase tracking-wide">Tags</dt>
                  <dd className="text-[#1A1615] mt-0.5">{fact.tags?.length || 0}</dd>
                </div>
                <div>
                  <dt className="text-[#7A6C5D] text-xs uppercase tracking-wide">Connections</dt>
                  <dd className="text-[#1A1615] mt-0.5">{fact.related_facts?.length || 0}</dd>
                </div>
              </dl>
            </div>
          </aside>
        </div>
      </article>
    </Layout>
  );
};

export default FactPage;
